// frontend/src/components/NotificationBell.jsx
import { useState, useEffect, useRef } from "react";
import {
  Bell,
  CheckCircle,
  X,
  AlertTriangle,
  Clock,
  Ban,
  Loader2,
  ChevronLeft,
  MessageCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../config/api";
import { useAuth } from "../context/AuthContext";

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (user) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [user]);

  // 🔥 FIX: CLICK OUTSIDE SOLO EN DESKTOP
  useEffect(() => {
    if (isMobile) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile]);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await api.get("/notifications");
      if (response.data.success) {
        setNotifications(response.data.data);
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    if (!user) return;
    try {
      const response = await api.get("/notifications/unread-count");
      if (response.data.success) {
        setUnreadCount(response.data.data.count);
      }
    } catch (error) {
      console.error("Error loading unread count:", error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(
        notifications.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(notifications.filter((n) => n.id !== id));
      const deleted = notifications.find((n) => n.id === id);
      if (deleted && !deleted.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleNotificationClick = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      setNotifications(
        notifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
    setIsOpen(false);
  };

  // 🔥 FIX: MANEJAR CLIC EN NOTIFICACIÓN DE CHAT
  const handleChatNotificationClick = (e, notification, conversationId) => {
    e.preventDefault();
    e.stopPropagation();

    handleNotificationClick(notification.id);
    window.location.href = `/chat/${conversationId}`;
  };

  const getIcon = (type) => {
    switch (type) {
      case "chat_message":
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case "temporary_ban":
        return <Clock className="w-5 h-5 text-orange-500" />;
      case "permanent_ban":
        return <Ban className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "chat_message":
        return "Mensagem";
      case "warning":
        return "Advertência";
      case "temporary_ban":
        return "Suspensão";
      case "permanent_ban":
        return "Banimento";
      default:
        return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "chat_message":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "warning":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      case "temporary_ban":
        return "bg-orange-50 text-orange-700 border-orange-200";
      case "permanent_ban":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  // 🔥 RENDERIZAR NOTIFICACIÓN (chat o sistema)
  const renderNotificationItem = (notification) => {
    const isChat = notification.type === "chat_message";
    const chatData = notification.data || {};

    if (isChat) {
      const conversationId = chatData.conversation_id || notification.id;

      return (
        <button
          type="button"
          onClick={(e) =>
            handleChatNotificationClick(e, notification, conversationId)
          }
          className="w-full text-left block p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 cursor-pointer"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {getIcon(notification.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-sm text-gray-900">
                  {notification.title}
                </p>
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteNotification(notification.id);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {notification.message}
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-xs text-gray-400">
                  {notification.time_ago}
                </span>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${getTypeColor(
                    notification.type
                  )}`}
                >
                  {getTypeLabel(notification.type)}
                </span>
                {!notification.is_read && (
                  <span className="text-[10px] text-blue-500 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Nova
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      );
    }

    // Notificaciones del sistema
    return (
      <div
        className={`p-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
          !notification.is_read
            ? "bg-blue-50/40 border-l-4 border-l-[#449879]"
            : ""
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">{getIcon(notification.type)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-sm text-gray-800">
                {notification.title}
              </p>
              <button
                onClick={() => deleteNotification(notification.id)}
                className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <p className="text-sm text-gray-600 whitespace-pre-wrap mt-1 leading-relaxed">
              {notification.message}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="text-xs text-gray-400">
                {notification.time_ago}
              </span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full border ${getTypeColor(
                  notification.type
                )}`}
              >
                {getTypeLabel(notification.type)}
              </span>
              {!notification.is_read && (
                <button
                  onClick={() => markAsRead(notification.id)}
                  className="text-xs text-[#449879] font-medium flex items-center gap-1"
                >
                  <CheckCircle className="w-3 h-3" />
                  Marcar como lida
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!user) return null;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* BOTÓN DE LA CAMPANA */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Notificações"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* PANEL DE NOTIFICACIONES - DROPDOWN ESTILO MOBILE */}
      {isOpen && (
        <>
          {/* Overlay para cerrar al tocar fuera (solo mobile) */}
          {isMobile && (
            <div
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setIsOpen(false)}
            />
          )}

          <div
            className={
              isMobile
                ? "fixed top-16 left-2 right-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[70vh] flex flex-col"
                : "absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-[500px] flex flex-col"
            }
          >
            {/* HEADER DEL PANEL */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50 flex-shrink-0">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#449879]" />
                Notificações
                {unreadCount > 0 && (
                  <span className="ml-1 text-xs bg-[#449879] text-white px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-[#449879] hover:underline font-medium"
                  >
                    Marcar todas
                  </button>
                )}
                {isMobile && (
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                    aria-label="Fechar"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            {/* LISTA DE NOTIFICACIONES */}
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 text-[#449879] animate-spin mx-auto" />
                <p className="text-sm text-gray-400 mt-2">Carregando...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-sm font-medium">Nenhuma notificação</p>
                <p className="text-xs text-gray-400 mt-1">
                  Você está em dia!
                </p>
              </div>
            ) : (
              <div className="overflow-y-auto flex-1">
                {notifications.map((notification) =>
                  renderNotificationItem(notification)
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}